import { FC, ReactNode, useEffect, useState } from 'react';
import { WithScope } from '@mews-commander/types';
import { SearchType } from '@mews-commander/enums';
import { createZodFormResolver, lt, useFormControls } from '@mews/framework';
import { useCommanderSelector, useGainsightCommanderEvents } from '@mews-commander/utils';
import { FormLayout } from '../../FormLayout';
import { SearchHookField } from '../../../../../components/form/hookFields';
import { getCompanyFormInitialValues } from '../selectors';
import { useRoutingRuleChangesConfirmation } from '../../../../routingRulesNew/components/RoutingRuleChangeConfirmationPanel/useRoutingRuleChangesConfirmation';
import { CompanyFormKeys, CompanyFormSchema, CompanyFormValues } from './types';
import { useChangeCompanyMutation } from './useChangeCompanyMutation';

interface CompanyFormProps extends WithScope {
    reservationIds: string[];
    onSuccess?: () => void;
}

export const CompanyForm: FC<CompanyFormProps> = ({ scope, reservationIds, onSuccess }: CompanyFormProps) => {
    const defaultValues = useCommanderSelector((state) => getCompanyFormInitialValues(state, { scope }));
    const changeCompanyMutation = useChangeCompanyMutation(reservationIds);
    const [routingRuleChangesDialog, setRoutingRuleChangesDialog] = useState<ReactNode>(null);

    const handleSubmit = (values: CompanyFormValues) => {
        const result = validate();

        if (result) {
            setRoutingRuleChangesDialog(result.dialog);
            return;
        }

        changeCompanyMutation.mutate(values, { onSuccess });
    };

    const { renderFormContent, watch, hasDirtyFields, reset } = useFormControls<CompanyFormValues>({
        onSubmit: handleSubmit,
        resolver: createZodFormResolver(CompanyFormSchema),
        defaultValues,
        dataTestForm: `company-form-${scope}`,
    });

    const companyId = watch(CompanyFormKeys.partnerCompanyId);

    useEffect(() => {
        reset(defaultValues);
    }, [reset, defaultValues]);

    const gainsightEvents = useGainsightCommanderEvents();

    const { validate, isLoading } = useRoutingRuleChangesConfirmation({
        reservationIds,
        partnerCompanyId: companyId,
        enabled: hasDirtyFields,
        scope,
        onSubmit: ({ changes }) => {
            const onInternalSuccess = () => {
                gainsightEvents.onApiSuccess({
                    context: 'BillingAutomationChanged',
                    data: {
                        changes,
                        origin: 'Company',
                    },
                });

                onSuccess?.();
            };

            changeCompanyMutation.mutate(watch(), { onSuccess: onInternalSuccess });
            setRoutingRuleChangesDialog(null);
        },
        onCancel: () => {
            reset();
            setRoutingRuleChangesDialog(null);
        },
    });

    return (
        <>
            {renderFormContent(
                <FormLayout loading={changeCompanyMutation.isPending} vertical={false} submitButtonLoading={isLoading}>
                    <SearchHookField
                        data-test-id={CompanyFormKeys.partnerCompanyId}
                        newEntity
                        name={CompanyFormKeys.partnerCompanyId}
                        label={lt('Company')}
                        searchTypes={[SearchType.Company]}
                    />
                </FormLayout>
            )}
            {routingRuleChangesDialog}
        </>
    );
};
